/**
 * street-drama-director.js
 * Đạo diễn hoạt cảnh 3D "Tranh Khách Vỉa Hè" (Street Drama Cutscene Engine)
 * Điều phối chính xác từng frame hành động, vị trí, biểu cảm, phụ đề,
 * camera cinematic và âm thanh va chạm cho 4 nhân vật.
 */

import * as THREE from 'three';
import { BlockyAvatar } from '../avatars/blocky-avatar.js';
import { StreetTeaStall } from './street-tea-stall.js';
import { SceneAudioManager } from '../audio/scene-audio-manager.js';
import { enableShadowCasting } from '../core/shadow-utils.js';
import { STREET_DRAMA_CUES } from './street-drama-cues.js';
import { applyScene1, applyScene2, applyScene3, applyScene4, applyScene5 } from './street-drama-timeline.js';
import { registerStreetDramaAudioMethods } from './street-drama-audio.js';

export { STREET_DRAMA_CUES };

export class StreetDramaDirector {
  constructor(scene, camera, controls) {
    this.scene = scene;
    this.camera = camera;
    this.controls = controls;

    this.group = new THREE.Group();
    this.group.name = 'StreetDramaScene';

    this.isPlaying = false;
    this.currentTime = 0;
    this.duration = 62.0; // 62 giây tổng thời lượng (62s Cinematic Edition)
    this.cameraMode = 'cinematic'; // 'cinematic' | 'free'

    // Điểm ngắm và vị trí mục tiêu cho Camera Cinematic (nội suy mượt mà lerp)
    this.targetCamPos = new THREE.Vector3(0.0, 2.8, 5.0);
    this.targetLookAt = new THREE.Vector3(0.0, 0.95, -3.8);

    this.isSoundEnabled = true;
    this.isTtsEnabled = true;
    this.audioManager = new SceneAudioManager({ isEnabled: this.isTtsEnabled });
    this.audioCtx = null;
    this.lastTriggeredSfx = {};
    this.lastSpokenKey = null;

    // Rung lắc camera khi có va chạm cú đấm
    this.screenShakeIntensity = 0;

    // Danh sách callbacks báo tiến trình lên UI
    this.onProgressCallbacks = [];

    // Khởi tạo các nhân vật và bối cảnh
    this.initActors();
    this.initAudioContext();
  }

  initActors() {
    // 1. Quán trà đá vỉa hè
    this.teaStall = new StreetTeaStall();
    this.teaStall.root.position.set(2.8, 0.22, -4.3);
    this.group.add(this.teaStall.root);

    // 2. Bốn diễn viên chính
    this.actors = {
      ba: new BlockyAvatar('BAC_BA_BA'),
      grab: new BlockyAvatar('TAI_XE_GRAB'),
      xanh: new BlockyAvatar('TAI_XE_XANH_SM'),
      trada: new BlockyAvatar('NGUOI_BAN_TRA_DA')
    };

    // Đổi tên để dễ nhận diện trong scene hierarchy
    this.actors.ba.root.name = 'Actor_BacBa';
    this.actors.grab.root.name = 'Actor_Grab';
    this.actors.xanh.root.name = 'Actor_XanhSM';
    this.actors.trada.root.name = 'Actor_BacTraDa';

    // Thêm các mesh vào group sân khấu (chỉ các bộ phận chính đổ bóng)
    Object.values(this.actors).forEach((avatar) => {
      enableShadowCasting(avatar.root, { minRadius: 0.15 });
      this.group.add(avatar.root);
    });

    // Định vị ban đầu
    this.seek(0, true);
  }

  // --- CỖ MÁY KỊCH BẢN TIMELINE 62S ĐIỆN ẢNH (CINEMATIC CUTSCENE) ---
  seek(t, isInstantCamera = false) {
    this.currentTime = Math.max(0, Math.min(this.duration, t));
    const time = this.currentTime;

    let sceneResult;
    if (time < 12.0) {
      sceneResult = applyScene1(this, time, this.actors);
    } else if (time < 24.0) {
      sceneResult = applyScene2(this, time, this.actors);
    } else if (time < 32.0) {
      sceneResult = applyScene3(this, time, this.actors);
    } else if (time < 44.0) {
      sceneResult = applyScene4(this, time, this.actors);
    } else {
      sceneResult = applyScene5(this, time, this.actors);
    }

    const { sceneIndex, subtitleText, speakerName } = sceneResult;

    // Nếu chuyển phân cảnh tức thời (click nút scene)
    if (isInstantCamera && this.cameraMode === 'cinematic') {
      this.camera.position.copy(this.targetCamPos);
      this.controls.target.copy(this.targetLookAt);
    }

    // Bắn sự kiện ra giao diện bên ngoài
    this.notifyProgress(sceneIndex, speakerName, subtitleText);
  }

  jumpToScene(sceneIndex) {
    this.stopTts();
    const timeStamps = [0.0, 12.0, 24.0, 32.0, 44.0];
    const targetTime = timeStamps[sceneIndex] !== undefined ? timeStamps[sceneIndex] : 0;
    this.lastTriggeredSfx = {};
    this.lastSpokenKey = null;
    this.seek(targetTime, true);
  }

  play() {
    this.isPlaying = true;
    this.resumeAudio();
    if (this.audioManager && typeof this.audioManager.unlock === 'function') {
      this.audioManager.unlock();
    }
  }

  pause() {
    this.isPlaying = false;
    this.stopTts();
  }

  restart() {
    this.stopTts();
    this.lastTriggeredSfx = {};
    this.lastSpokenKey = null;
    this.seek(0, true);
    this.play();
  }

  update(delta) {
    if (this.isPlaying) {
      this.currentTime += delta;
      if (this.currentTime >= this.duration) {
        this.currentTime = this.duration;
        this.isPlaying = false;
        this.stopTts();
      }
      this.seek(this.currentTime, false);
    }

    // Cập nhật hoạt hình cho các avatar
    Object.values(this.actors).forEach((avatar) => avatar.update(delta));

    // Nội suy mượt mà Camera Cinematic
    if (this.cameraMode === 'cinematic') {
      const lerpSpeed = Math.min(1.0, delta * 3.5);
      this.camera.position.lerp(this.targetCamPos, lerpSpeed);
      this.controls.target.lerp(this.targetLookAt, lerpSpeed);

      // Xử lý rung chấn camera nếu có cú đấm hoặc cú ngã
      if (this.screenShakeIntensity > 0) {
        const prefersReducedMotion =
          typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (!prefersReducedMotion) {
          const shakeX = (Math.random() - 0.5) * this.screenShakeIntensity;
          const shakeY = (Math.random() - 0.5) * this.screenShakeIntensity;
          this.camera.position.x += shakeX;
          this.camera.position.y += shakeY;
        }
        this.screenShakeIntensity = Math.max(0, this.screenShakeIntensity - delta * 0.8);
      }
    }
  }

  onProgress(callback) {
    this.onProgressCallbacks.push(callback);
  }

  notifyProgress(sceneIndex, speaker, subtitle) {
    const audioSource = this.audioManager ? this.audioManager.getSourceType(this.lastSpokenKey) : 'edge_tts';
    this.onProgressCallbacks.forEach((cb) => {
      try {
        cb({
          currentTime: this.currentTime,
          duration: this.duration,
          sceneIndex,
          speaker,
          subtitle,
          isPlaying: this.isPlaying,
          cameraMode: this.cameraMode,
          audioSource,
          cueId: this.lastSpokenKey
        });
      } catch {}
    });
  }

  dispose() {
    this.pause();
    if (this.audioManager) this.audioManager.dispose();
    if (this.teaStall) this.teaStall.dispose();
    Object.values(this.actors).forEach((avatar) => avatar.dispose());
    if (this.sfx) {
      this.sfx.dispose();
      this.audioCtx = null;
    }
  }
}

registerStreetDramaAudioMethods(StreetDramaDirector.prototype);
